import React, { useEffect, useRef, useState } from 'react';
import { FaGraduationCap, FaBriefcase, FaCode } from 'react-icons/fa';
import profile from '../data/profile';
import useLocale from '../i18n/useLocale';
import Reveal from './Reveal';

const SkillBar = ({ name, level, idx }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="skill-card glass" style={{ transitionDelay: `${idx * 40}ms` }}>
      <div className="skill-head">
        <span className="skill-name">{name}</span>
        <span className="skill-level">{level}%</span>
      </div>
      <div className="skill-bar">
        <span
          className="skill-fill"
          style={{
            width: active ? `${level}%` : '0%',
            transitionDelay: `${100 + idx * 40}ms`,
          }}
        />
      </div>
    </div>
  );
};

const Resume = () => {
  const { L } = useLocale();
  const education = L.resume.education;
  const work = L.resume.work;

  return (
    <section id="resume" className="section resume-section">
      <div className="container">
        <Reveal className="section-head">
          <span className="section-kicker">02 / Resume</span>
          <h2 className="section-title">
            {L.resume.workTitle}<span className="accent">.</span>
          </h2>
        </Reveal>

        <div className="resume-block">
          <Reveal className="resume-heading">
            <FaBriefcase />
            <h3>{L.resume.workTitle}</h3>
          </Reveal>

          <div className="timeline">
            {work.map((item, idx) => (
              <Reveal
                key={`${item.company}-${idx}`}
                className="timeline-item"
                x={idx % 2 === 0 ? -30 : 30}
                y={0}
                delay={idx * 60}
              >
                <div className="timeline-dot" />
                <div className="timeline-card glass">
                  <div className="timeline-card-head">
                    <h4>{item.title}</h4>
                    <span className="timeline-years">{item.years}</span>
                  </div>
                  {profile.companyLogos[item.slug] ? (
                    profile.companyUrls[item.slug] ? (
                      <a
                        href={profile.companyUrls[item.slug]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="timeline-logo-link clickable"
                        aria-label={item.company}
                      >
                        <img
                          src={profile.companyLogos[item.slug]}
                          alt={item.company}
                          className="timeline-logo"
                          loading="lazy"
                        />
                      </a>
                    ) : (
                      <img
                        src={profile.companyLogos[item.slug]}
                        alt={item.company}
                        className="timeline-logo"
                        loading="lazy"
                      />
                    )
                  ) : (
                    <h5 className="timeline-company">{item.company}</h5>
                  )}
                  <p>{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="resume-block">
          <Reveal className="resume-heading">
            <FaGraduationCap />
            <h3>{L.resume.educationTitle}</h3>
          </Reveal>

          <div className="edu-grid">
            {education.map((e, idx) => (
              <Reveal key={e.school} delay={idx * 80}>
                <div className="edu-card glass">
                  <h4>{e.school}</h4>
                  <p className="info">
                    <span>{e.degree}</span> · <em>{e.graduated}</em>
                  </p>
                  <p>{e.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="resume-block">
          <Reveal className="resume-heading">
            <FaCode />
            <h3>{L.resume.skillsTitle}</h3>
          </Reveal>

          <Reveal>
            <p className="skill-message">{L.resume.skillMessage}</p>
          </Reveal>

          <div className="skill-grid">
            {profile.skills.map((s, i) => (
              <SkillBar key={s.name} name={s.name} level={s.level} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resume;
